import styles from "./InspectOverlay.module.scss";
import classNames from "classnames";

import { Feature } from "ol";

import { TEXT_TYPES } from "../types/text.type";
import { ICON_CHECK } from "../icons/check";
import { useFeatureStateSync } from "../hooks/useFeatureStateSync";

export type ChangesState = "PENDING" | "HIDDEN" | "CONFIRMED";

export type ServerChangeObject = {
  id: number;
  pnu: string;
  continuous_cadastral_map_id: number;
  area: number;
  sido_id: number;
  sgg_id: number;
  umd_id: number;
  state: ChangesState;
  legal_dong_id: number;
  center_latitude: number;
  center_longitude: number;
};

export type FrontChangeObject = ServerChangeObject & {
  changesType: string;
};

export type DetectionObjectClientStatus =
  keyof typeof DetectionObjectClientToServer;
export type DetectionObjectServerStatus =
  (typeof DetectionObjectClientToServer)[DetectionObjectClientStatus];

export const DetectionObjectClientToServer = {
  Pending: "PENDING",
  Confirmed: "CORRECT",
  Incorrect: "INCORRECT",
  Uncertain: "UNCERTAIN",
} as const;

export const DetectionObjectServerToClient = {
  PENDING: "Pending",
  CORRECT: "Confirmed",
  INCORRECT: "Incorrect",
  UNCERTAIN: "Uncertain",
} as const;

export function toServerDetectionObjectStatus(
  status: DetectionObjectClientStatus
): DetectionObjectServerStatus {
  return DetectionObjectClientToServer[status];
}

export function toClientDetectionObjectStatus(
  status: DetectionObjectServerStatus
): DetectionObjectClientStatus {
  return DetectionObjectServerToClient[status];
}

interface Props {
  feature: Feature;
}

export default function InspectOverlay({ feature }: Props) {
  const { state, updateState } = useFeatureStateSync(feature);

  const handleClickEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    updateState(event.currentTarget.value);
  };

  const status = feature.get("validation_status");

  if (status === "DELETE") return <></>;

  return (
    <div
      className={classNames(
        styles["inspect-overlay__container"],
        TEXT_TYPES["H5_500"]
      )}
    >
      {status === "NOT_DETECTED" ? (
        <>
          <button
            className={classNames(styles["__wrapper"])}
            onClick={handleClickEvent}
            value={"DELETE"}
          >
            <div
              className={classNames(styles["__label"], TEXT_TYPES["H5_500"])}
            >
              DELETE
            </div>
          </button>
        </>
      ) : (
        <>
          <button
            className={classNames(
              styles["__wrapper"],
              state === DetectionObjectClientToServer["Confirmed"] &&
                styles["__selected"]
            )}
            onClick={handleClickEvent}
            value={DetectionObjectClientToServer["Confirmed"]}
          >
            <div className={styles["__img"]}>
              {state === DetectionObjectClientToServer["Confirmed"] && (
                <img src={ICON_CHECK.DEFAULT_WHITE} />
              )}
            </div>
            <div
              className={classNames(
                styles["__label"],
                state === DetectionObjectClientToServer["Incorrect"]
                  ? TEXT_TYPES["H5_700"]
                  : TEXT_TYPES["H5_500"]
              )}
            >
              {DetectionObjectServerToClient["CORRECT"]}
            </div>
          </button>
          <button
            className={classNames(
              styles["__wrapper"],
              state === DetectionObjectClientToServer["Incorrect"] &&
                styles["__selected"]
            )}
            onClick={handleClickEvent}
            value={DetectionObjectClientToServer["Incorrect"]}
          >
            <div className={styles["__img"]}>
              {state === DetectionObjectClientToServer["Incorrect"] && (
                <img src={ICON_CHECK.DEFAULT_WHITE} />
              )}
            </div>
            <div
              className={classNames(
                styles["__label"],
                state === DetectionObjectClientToServer["Incorrect"]
                  ? TEXT_TYPES["H5_700"]
                  : TEXT_TYPES["H5_500"]
              )}
            >
              {DetectionObjectServerToClient["INCORRECT"]}
            </div>
          </button>
          <button
            className={classNames(
              styles["__wrapper"],
              state === DetectionObjectClientToServer["Uncertain"] &&
                styles["__selected"]
            )}
            onClick={handleClickEvent}
            value={DetectionObjectClientToServer["Uncertain"]}
          >
            <div className={styles["__img"]}>
              {state === DetectionObjectClientToServer["Uncertain"] && (
                <img src={ICON_CHECK.DEFAULT_WHITE} />
              )}
            </div>
            <div
              className={classNames(
                styles["__label"],
                state === DetectionObjectClientToServer["Incorrect"]
                  ? TEXT_TYPES["H5_700"]
                  : TEXT_TYPES["H5_500"]
              )}
            >
              {DetectionObjectServerToClient["UNCERTAIN"]}
            </div>
          </button>
        </>
      )}
    </div>
  );
}
